import { 
  FontAlignments, 
  FontIcons, 
  FontWeights, 
  StandardButton, 
  StandardDialog, 
  StandardSwitch, 
  StandardTabs, 
  StandardText, 
  StandardTextfield 
} from '@brandfolder/react';
import { slugify } from '@brandfolder/utilities';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { boolean, object, SchemaOf, string } from 'yup';

import { useFetch, UseFetchOptions } from '@api/ApiHelper';
import { ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { BrandguidePost } from '@api/v4/resources/brandguides';
import { BrandguidesCombobox } from '@components/brandguides/BrandguidesCombobox';
import { LabelPosition } from '@components/library/switch';
import { getStandardDialogLabels } from '@translations';

import classes from './styles/brandguide-create-form.module.scss';

const errorMessages = {
  nameRequired: bfTranslate('Name required'),
  slugRequired: bfTranslate('Slug required')
};

const defaultValues: BrandguidePost = {
  name: '',
  slug: '',
  public: true
};

const schema: SchemaOf<BrandguidePost> = object().shape({
  name: string().required(errorMessages.nameRequired),
  public: boolean().required(),
  slug: string().required(errorMessages.slugRequired)
});

interface BrandguideCreateFormProps {
  open: boolean;
  organizationKey: string;
  setOpen: SetStateDispatch<boolean>;
  isOrg?: boolean;
}

export type ResourceType = 'brandfolder' | 'collection';

export const BrandguideCreateForm: FunctionComponent<BrandguideCreateFormProps> = (props) => {
  const { isOrg, open, organizationKey, setOpen } = props;

  const [isChecked, setIsChecked] = useState(defaultValues.public);
  const [resourceKey, setResourceKey] = useState('');
  const [resourceType, setResourceType] = useState<ResourceType>('brandfolder');

  const fetchOptions: UseFetchOptions = {
    fetchOnMount: false,
    url: `/api/v4/${isOrg ? resourceType : BFG.resource.type}s/${isOrg ? resourceKey : BFG.resource.key}/brandguides`,
    method: 'POST'
  };

  const { error, loading, fetch, response } = useFetch<ApiDatumResponse<BrandguidePost, 'brandguides'>>(fetchOptions);

  const { formState, handleSubmit, register, reset, setValue } = useForm<BrandguidePost>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { onChange, ...nameProps } = register('name');
  // ref is unused because <StandardSwitch> can't accept a ref
  const { ref, ...publicProps } = register('public');

  const { errors, touchedFields } = formState;

  const handleClose = (): void => {
    reset();
    setOpen(false);
  };

  useEffect(() => {
    setValue('public', isChecked);
  }, [isChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (response?.data) {
      const { name, slug } = response.data.attributes;

      Notify.create({
        body: `<a href="${BFG.brandguideUrl}/${slug}">${bfTranslate('Visit')} ${name}</a>`,
        title: bfTranslate('Brandguide created successfully!'),
        type: 'success'
      });

      handleClose();
    }
  }, [response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      Notify.create({
        title: bfTranslate('Error creating your Brandguide. Please try again.'),
        type: 'error'
      });
    }
  }, [error]);

  return (
    <StandardDialog
      className={classes.dialog}
      id="create-brandguide-dialog"
      labels={getStandardDialogLabels()}
      open={open}
      setOpen={handleClose}
      showFooter={false}
      title={bfTranslate('Create New Brandguide')}
      titleIcon={FontIcons.Plus}
      width={700}
    >
      {isOrg && (
        <StandardTabs 
          caption={bfTranslate('Select between displaying brandfolder resources or collection resources')} 
          className={classes.brandfolderCollectionTabs}
          id="brandfolder-collection-tabs" 
          onChange={(nextIndex): void=> {
            if (nextIndex === 0) {
              setResourceType('brandfolder');
            } else {
              setResourceType('collection');
            }
          }}
          showUnderline
          tabPanelProps={{ active: null, children: '', className: classes.brandguideTabPanel, id: 'tab-panels', tabButtonId: 'tab-button' }}
          tabs={[
            { 
              tabButton: bfTranslate('Brandfolder'),
              tabPanel: (
                <BrandguidesCombobox organizationKey={organizationKey} setResourceKey={setResourceKey} type="brandfolder" />
              )
            },
            {
              tabButton: bfTranslate('Collection'),
              tabPanel: (
                <BrandguidesCombobox organizationKey={organizationKey} setResourceKey={setResourceKey} type="collection" />
              )
            }
          ]}
        />
      )}
      {isOrg && (<hr className={classes.line}></hr>)}
      <form
        className={classes.form}
        noValidate
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit((values) => {
          fetch({ ...fetchOptions,
            body: {
              data: {
                attributes: {
                  ...values
                }
              }
            }
          });
        })}
      >
        <StandardTextfield
          {...nameProps}
          className={classes.textfield}
          disabled={loading}
          error={errors.name?.message}
          id="create-brandguide-name"
          label={bfTranslate('Brandguide Name')}
          onChange={(e): void => {
            onChange(e);

            if (!touchedFields.slug) {
              setValue('slug', slugify(e.target.value), {
                shouldDirty: true,
                shouldTouch: false,
                shouldValidate: false
              });
            }
          }}
          required
          requiredLabel={bfTranslate('Required')}
        />
        <div className={classes.formControl}>
          <StandardTextfield
            {...register('slug')}
            before={`${BFG.brandguideUrl}/`}
            beforeProps={{ className: classes.before }}
            className={classes.textfield}
            disabled={loading}
            error={errors.slug?.message}
            id="create-brandguide-slug"
            label={bfTranslate('Brandguide URL')}
            placeholder={bfTranslate('brandguide-name')}
            required
            requiredLabel={bfTranslate('Required')}
          />
        </div>
        <div className={classes.formControl}>
        <StandardText align={FontAlignments.Left} weight={FontWeights.Bold}>
          {bfTranslate('Privacy')}
        </StandardText>
          <StandardSwitch
            {...publicProps}
            id="create-brandguide-public"
            isChecked={isChecked}
            isDisabled={loading}
            labelPosition={LabelPosition.Right}
            onChange={(): void => {
              setIsChecked(!isChecked);
            }}
          >
            {bfTranslate('Public')}
          </StandardSwitch>
        </div>
        <div className={classes.submitButton}>
          <StandardButton
            disabled={loading}
            id="create-brandguide-submit"
            type="submit"
          >
            {bfTranslate('Create Brandguide')}
          </StandardButton>
        </div>
      </form>
    </StandardDialog>
  );
};
